import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { authApi } from '@auth/api/auth';
import { User } from '@auth/types/auth';

const buildUser = (user: User) => {
  const currentOrg = user.organizations.find((org) => org.isCurrent) ?? user.organizations[0];

  return {
    ...user,
    currentOrg,
  };
};

/**
 * This hook is used to check the authentication status of the user.
 * It will return the user object if the user is authenticated, or null if the user is not authenticated.
 * If the user is not authenticated, it will ping the server to check if the server is responsive.
 * If the server is not responsive, it will return null.
 * If the server is responsive, it will return the user object.
 * @param retry - Whether this is a retry request.
 */
export const useAuthStatus = ({ retry = false }: { retry?: boolean } = {}) => {
  const location = useLocation();
  const token = localStorage.getItem('token');

  return useQuery({
    queryKey: ['authStatus', token],
    queryFn: async () => {
      if (location.pathname === '/connection-error' && !retry) {
        return token ? ({} as User) : null;
      }

      if (!token) {
        await authApi.pingServer();
        return null;
      }

      const data = await authApi.validateToken();
      return buildUser(data);
    },
    retry: 1,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};
